import { useCallback, useEffect, useRef, useState } from 'react';

interface ReorderableDNDProps<T> {
  items: T[];
  onReorder: (newItems: T[]) => void;
  itemKey: (item: T) => string | number;
}

interface DragState {
  isDragging: boolean;
  dragIndex: number | null;
  dragOverIndex: number | null;
}

export const useReorderableDND = <T>({ items, onReorder, itemKey }: ReorderableDNDProps<T>) => {
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    dragIndex: null,
    dragOverIndex: null,
  });
  
  const containerRef = useRef<HTMLDivElement>(null);
  const dropIndicatorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    
    let indicator = containerRef.current.querySelector('.drop-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'drop-indicator';
      containerRef.current.appendChild(indicator);
    }
    
    dropIndicatorRef.current = indicator as HTMLDivElement;
    
    return () => {
      dropIndicatorRef.current?.remove();
    };
  }, []);

  useEffect(() => {
    const indicator = dropIndicatorRef.current;
    if (!indicator || !containerRef.current || dragState.dragOverIndex === null) {
      if (indicator) indicator.style.display = 'none';
      return;
    }

    const container = containerRef.current;
    const children = Array.from(container.children).filter(
      child => !child.classList.contains('drop-indicator')
    ) as HTMLElement[];
    
    indicator.style.display = 'block';
    
    if (dragState.dragOverIndex >= children.length) {
      const lastChild = children[children.length - 1];
      if (lastChild) {
        const rect = lastChild.getBoundingClientRect();
        indicator.style.top = `${rect.bottom - container.getBoundingClientRect().top}px`;
      } else {
        indicator.style.top = '0px';
      }
    } else {
      const targetChild = children[dragState.dragOverIndex];
      const rect = targetChild.getBoundingClientRect();
      indicator.style.top = `${rect.top - container.getBoundingClientRect().top}px`;
    }
  }, [dragState.dragOverIndex]);

  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    
    const dragEl = e.currentTarget;
    
    const emptyImg = new Image();
    emptyImg.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    e.dataTransfer.setDragImage(emptyImg, 0, 0);
    
    setDragState({
      isDragging: true,
      dragIndex: index,
      dragOverIndex: index,
    });
    
    setTimeout(() => {
      if (dragEl) {
        dragEl.style.opacity = '0.4';
      }
    }, 0);
  }, []);
  
  const calculateDropIndex = useCallback((e: React.DragEvent<HTMLDivElement>, containerEl: HTMLDivElement): number | null => {
    if (!containerEl || dragState.dragIndex === null) return null;

    const rect = containerEl.getBoundingClientRect();
    const mousePos = e.clientY - rect.top;

    const children = Array.from(containerEl.children).filter(
      child => !child.classList.contains('drop-indicator')
    ) as HTMLElement[];
    
    let targetIndex = items.length;

    for (let i = 0; i < children.length; i++) {
        if (i === dragState.dragIndex) continue;

        const child = children[i];
        const childRect = child.getBoundingClientRect();
        const childStart = childRect.top - rect.top;
        const childMidpoint = childStart + childRect.height / 2;
        
        const dataIndex = child.dataset.index;
        const originalIndexOfChild = dataIndex ? parseInt(dataIndex, 10) : i;

        if (mousePos < childMidpoint) {
            targetIndex = originalIndexOfChild;
            break;
        }
    }

    return targetIndex;
  }, [dragState.dragIndex, items]);
  
  const handleDragOverItem = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (dragState.dragIndex === null) return;
    
    const containerEl = containerRef.current;
    if (!containerEl) return;

    const dropIndex = calculateDropIndex(e, containerEl);

    if (dropIndex !== null && dropIndex !== dragState.dragOverIndex) {
        setDragState(prev => ({
          ...prev,
          dragOverIndex: dropIndex
        }));
    }
  }, [dragState.dragIndex, dragState.dragOverIndex, calculateDropIndex]);

  const handleDragOverContainer = useCallback((e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (dragState.dragIndex === null) return;

      const containerEl = containerRef.current;
      if (!containerEl) return;

      const dropIndex = calculateDropIndex(e, containerEl);

      if (dropIndex !== null && dropIndex !== dragState.dragOverIndex) {
          setDragState(prev => ({
            ...prev,
            dragOverIndex: dropIndex
          }));
      }
  }, [dragState.dragIndex, dragState.dragOverIndex, calculateDropIndex]);
  
  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    const dragEl = containerRef.current?.children[dragState.dragIndex ?? -1] as HTMLElement | undefined;
    if (dragEl) dragEl.style.opacity = '1';
    
    const { dragIndex, dragOverIndex } = dragState;
    
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newItems = [...items];
      const draggedItem = newItems[dragIndex];
      
      newItems.splice(dragIndex, 1);
      
      let adjustedDropIndex = dragOverIndex;
      if (dragOverIndex > dragIndex) {
        adjustedDropIndex--;
      }
      
      adjustedDropIndex = Math.max(0, Math.min(newItems.length, adjustedDropIndex));
      newItems.splice(adjustedDropIndex, 0, draggedItem);
      
      onReorder(newItems);
    }
    
    setDragState({
      isDragging: false,
      dragIndex: null,
      dragOverIndex: null,
    });
    
    if (dropIndicatorRef.current) {
      dropIndicatorRef.current.style.display = 'none';
    }
  }, [dragState, items, onReorder]);
  
  const getItemProps = useCallback((item: T, index: number) => {
    return {
      key: itemKey(item),
      draggable: true,
      onDragStart: (e: React.DragEvent<HTMLDivElement>) => handleDragStart(e, index),
      onDragOver: (e: React.DragEvent<HTMLDivElement>) => handleDragOverItem(e),
      onDragEnd: (e: React.DragEvent<HTMLDivElement>) => handleDragEnd(e),
      style: {
        opacity: dragState.dragIndex === index ? 0.4 : 1,
        cursor: 'grab',
      },
      'data-index': index,
    };
  }, [dragState, handleDragStart, handleDragOverItem, handleDragEnd, itemKey]);
  
  const getContainerProps = useCallback(() => {
    return {
      ref: containerRef,
      onDragOver: handleDragOverContainer,
      style: {
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      } as React.CSSProperties,
    };
  }, [handleDragOverContainer]);
  
  return {
    containerRef,
    dragState,
    getItemProps,
    getContainerProps,
  };
}; 